import { getAuthHeaders } from "./authToken";

// Utility function to get API base URL
export const getApiBaseUrl = () => {
  if (import.meta.env.VITE_APP_API_BASE_URL) {
    return import.meta.env.VITE_APP_API_BASE_URL;
  }

  if (typeof window !== "undefined") {
    return window.location.origin;
  }

  return "https://velasquez-home.com";
};

const fetchWithAuth = async (url, options = {}) => {
  const authHeaders = await getAuthHeaders();
  return fetch(url, {
    ...options,
    headers: {
      ...authHeaders,
      ...options.headers,
    },
  });
};

// Helper function to handle API responses
const handleApiResponse = async (response) => {
  if (!response.ok) {
    throw new Error(
      `API request failed: ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
};

// Helper function to build query string from parameters
const buildQueryString = (params) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      searchParams.append(key, value);
    }
  });
  return searchParams.toString();
};

// API service functions
export const api = {
  // Transactions
  getTransactions: async (params = {}) => {
    const baseUrl = getApiBaseUrl();
    const queryString = buildQueryString(params);
    const response = await fetchWithAuth(
      `${baseUrl}/api/transactions${queryString ? `?${queryString}` : ""}`,
    );
    return handleApiResponse(response);
  },

  // Financial Summary
  getFinancialSummary: async (params = {}) => {
    const baseUrl = getApiBaseUrl();
    const queryString = buildQueryString(params);
    const response = await fetchWithAuth(
      `${baseUrl}/api/financial-summary${queryString ? `?${queryString}` : ""}`,
    );
    return handleApiResponse(response);
  },

  // Category Spending
  getCategorySpending: async (params = {}) => {
    const baseUrl = getApiBaseUrl();
    const queryString = buildQueryString(params);
    const response = await fetchWithAuth(
      `${baseUrl}/api/category-spending${queryString ? `?${queryString}` : ""}`,
    );
    return handleApiResponse(response);
  },

  // Sub-Category Spending
  getSubCategorySpending: async (params = {}) => {
    const baseUrl = getApiBaseUrl();
    const queryString = buildQueryString(params);
    const response = await fetchWithAuth(
      `${baseUrl}/api/subcategory-spending${queryString ? `?${queryString}` : ""
      }`,
    );
    return handleApiResponse(response);
  },

  // Historical Category Averages (configurable period)
  getCategoryHistoricalAverage: async (params = {}) => {
    const baseUrl = getApiBaseUrl();
    const queryString = buildQueryString(params);
    const response = await fetchWithAuth(
      `${baseUrl}/api/category-historical-average${queryString ? `?${queryString}` : ""}`,
    );
    return handleApiResponse(response);
  },

  // 90-Day Category Averages (deprecated, use getCategoryHistoricalAverage with days=90)
  getCategory90DayAverage: async (params = {}) => {
    const baseUrl = getApiBaseUrl();
    const queryString = buildQueryString(params);
    const response = await fetchWithAuth(
      `${baseUrl}/api/category-90day-average${queryString ? `?${queryString}` : ""}`,
    );
    return handleApiResponse(response);
  },

  // Daily Spending
  getDailySpending: async (params = {}) => {
    const baseUrl = getApiBaseUrl();
    const queryString = buildQueryString(params);
    const response = await fetchWithAuth(
      `${baseUrl}/api/daily-spending${queryString ? `?${queryString}` : ""}`,
    );
    return handleApiResponse(response);
  },

  // Budget Comparison
  getBudgetComparison: async (params = {}) => {
    const baseUrl = getApiBaseUrl();
    const queryString = buildQueryString(params);
    const response = await fetchWithAuth(
      `${baseUrl}/api/budget-comparison${queryString ? `?${queryString}` : ""}`,
    );
    return handleApiResponse(response);
  },

  // Financial Advisor AI Analysis
  getFinancialAdvisor: async (params = {}) => {
    const baseUrl = getApiBaseUrl();
    const queryString = buildQueryString(params);
    const response = await fetchWithAuth(
      `${baseUrl}/api/financial-advisor${queryString ? `?${queryString}` : ""}`,
    );
    return handleApiResponse(response);
  },

  // Categories
  getCategories: async () => {
    const baseUrl = getApiBaseUrl();
    const response = await fetchWithAuth(`${baseUrl}/api/categories`);
    return handleApiResponse(response);
  },

  // Keywords
  getKeywords: async () => {
    const baseUrl = getApiBaseUrl();
    const response = await fetchWithAuth(`${baseUrl}/api/keywords`);
    return handleApiResponse(response);
  },

  addKeyword: async (keyword, category, person = null) => {
    const baseUrl = getApiBaseUrl();
    const response = await fetchWithAuth(`${baseUrl}/api/keywords`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ keyword, category, person }),
    });
    return handleApiResponse(response);
  },

  updateKeyword: async (id, keyword, category, person = null) => {
    const baseUrl = getApiBaseUrl();
    const response = await fetchWithAuth(`${baseUrl}/api/keywords/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ keyword, category, person }),
    });
    return handleApiResponse(response);
  },

  deleteKeyword: async (id) => {
    const baseUrl = getApiBaseUrl();
    const response = await fetchWithAuth(`${baseUrl}/api/keywords/${id}`, {
      method: "DELETE",
    });
    return handleApiResponse(response);
  },

  // Monthly Expenses
  getMonthlyExpenses: async () => {
    const baseUrl = getApiBaseUrl();
    const response = await fetchWithAuth(`${baseUrl}/api/monthly-expenses`);
    return handleApiResponse(response);
  },

  // Monthly expenses individual operations
  addMonthlyExpense: async (name, amount, category = '') => {
    const baseUrl = getApiBaseUrl();
    const response = await fetchWithAuth(`${baseUrl}/api/monthly-expenses`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, amount, category }),
    });
    return handleApiResponse(response);
  },

  updateMonthlyExpense: async (id, name, amount, category = '') => {
    const baseUrl = getApiBaseUrl();
    const response = await fetchWithAuth(`${baseUrl}/api/monthly-expenses/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, amount, category }),
    });
    return handleApiResponse(response);
  },

  deleteMonthlyExpense: async (id) => {
    const baseUrl = getApiBaseUrl();
    const response = await fetchWithAuth(`${baseUrl}/api/monthly-expenses/${id}`, {
      method: "DELETE",
    });
    return handleApiResponse(response);
  },

  // Monthly income individual operations
  addMonthlyIncome: async (name, amount, category = '') => {
    const baseUrl = getApiBaseUrl();
    const response = await fetchWithAuth(`${baseUrl}/api/monthly-income`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, amount, category }),
    });
    return handleApiResponse(response);
  },

  updateMonthlyIncome: async (id, name, amount, category = '') => {
    const baseUrl = getApiBaseUrl();
    const response = await fetchWithAuth(`${baseUrl}/api/monthly-income/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, amount, category }),
    });
    return handleApiResponse(response);
  },

  deleteMonthlyIncome: async (id) => {
    const baseUrl = getApiBaseUrl();
    const response = await fetchWithAuth(`${baseUrl}/api/monthly-income/${id}`, {
      method: "DELETE",
    });
    return handleApiResponse(response);
  },

  // Monthly budget individual operations
  addMonthlyBudget: async (category, amount) => {
    const baseUrl = getApiBaseUrl();
    const response = await fetchWithAuth(`${baseUrl}/api/monthly-budget`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ category, amount }),
    });
    return handleApiResponse(response);
  },

  updateMonthlyBudget: async (id, category, amount) => {
    const baseUrl = getApiBaseUrl();
    const response = await fetchWithAuth(`${baseUrl}/api/monthly-budget/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ category, amount }),
    });
    return handleApiResponse(response);
  },

  deleteMonthlyBudget: async (id) => {
    const baseUrl = getApiBaseUrl();
    const response = await fetchWithAuth(`${baseUrl}/api/monthly-budget/${id}`, {
      method: "DELETE",
    });
    return handleApiResponse(response);
  },

  saveKeywords: async (keywords) => {
    const baseUrl = getApiBaseUrl();
    const response = await fetchWithAuth(`${baseUrl}/api/save-keywords`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ keywords }),
    });
    return handleApiResponse(response);
  },

  saveMonthlyExpenses: async (data) => {
    const baseUrl = getApiBaseUrl();
    const response = await fetchWithAuth(`${baseUrl}/api/save-monthly-expenses`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    return handleApiResponse(response);
  },

  uploadKeywords: async (formData) => {
    const baseUrl = getApiBaseUrl();

    // Create AbortController for 3-minute timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3 * 60 * 1000); // 3 minutes

    try {
      const response = await fetchWithAuth(`${baseUrl}/api/upload-keywords`, {
        method: "POST",
        body: formData, // FormData for file upload
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      return handleApiResponse(response);
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Keywords upload timed out after 3 minutes.');
      }
      throw error;
    }
  },

  previewAmazonOrderMatch: async (person, orders) => {
    const baseUrl = getApiBaseUrl();
    const response = await fetchWithAuth(`${baseUrl}/api/amazon-order-match/preview`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ person, orders }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || `Preview failed: ${response.status}`);
    }
    return data;
  },

  applyAmazonOrderMatch: async (person, orders) => {
    const baseUrl = getApiBaseUrl();
    const response = await fetchWithAuth(`${baseUrl}/api/amazon-order-match/apply`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ person, orders }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.error || `Apply failed: ${response.status}`);
    }
    return data;
  },

  recategorizeTransactions: async (params = {}) => {
    const baseUrl = getApiBaseUrl();

    // Create an AbortController for longer timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10 * 60 * 1000); // 10 minutes

    try {
      const response = await fetchWithAuth(`${baseUrl}/api/recategorize-transactions`, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          forceRecategorize: params.forceRecategorize || false,
          startDate: params.startDate || null,
          endDate: params.endDate || null,
          limit: params.limit || null
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      return handleApiResponse(response);
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Re-categorization timed out after 10 minutes.');
      }
      throw error;
    }
  },

  deleteTransaction: async (transactionId) => {
    const baseUrl = getApiBaseUrl();
    const response = await fetchWithAuth(`${baseUrl}/api/transactions/${transactionId}`, {
      method: "DELETE",
    });
    return handleApiResponse(response);
  },

  // DEPRECATED: Transaction upload functions - redirected to safe append
  // NOTE: This used to be dangerous and replace all data
  uploadTransactions: async (transactions) => {
    console.warn("⚠️  DEPRECATED: uploadTransactions redirected to appendTransactions for safety");
    console.warn("📧 Please update your code to use appendTransactions() directly");

    // Redirect to safe append function
    return api.appendTransactions(transactions);
  },

  appendTransactions: async (transactions, options = {}) => {
    const baseUrl = getApiBaseUrl();
    const { skipCategorization = false, superFastMode = false, timeout = 5 * 60 * 1000 } = options;

    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetchWithAuth(`${baseUrl}/api/append-transactions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ 
          transactions,
          skipCategorization,
          superFastMode
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      return handleApiResponse(response);
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        const timeoutMinutes = Math.round(timeout / 60000);
        throw new Error(`Transaction upload timed out after ${timeoutMinutes} minutes. Try uploading fewer transactions at once or use fast/super fast mode.`);
      }
      throw error;
    }
  },

  // Add individual transaction
  addTransaction: async (transaction) => {
    const baseUrl = getApiBaseUrl();

    // Create AbortController for 2-minute timeout (individual transactions should be faster)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2 * 60 * 1000); // 2 minutes

    try {
      const response = await fetchWithAuth(`${baseUrl}/api/transactions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(transaction),
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      return handleApiResponse(response);
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Transaction creation timed out after 2 minutes.');
      }
      throw error;
    }
  },

  // Update individual transaction
  updateSingleTransaction: async (transaction) => {
    const baseUrl = getApiBaseUrl();
    const response = await fetchWithAuth(`${baseUrl}/api/update-single-transaction`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ transaction }),
    });
    return handleApiResponse(response);
  },

  // Get transaction history (audit trail)
  getTransactionHistory: async (transactionId) => {
    const baseUrl = getApiBaseUrl();
    const response = await fetchWithAuth(`${baseUrl}/api/transactions/${transactionId}/history`);
    return handleApiResponse(response);
  },

  // Plaid API methods
  createPlaidLinkToken: async (userId = null) => {
    const baseUrl = getApiBaseUrl();
    const response = await fetchWithAuth(`${baseUrl}/api/plaid/create-link-token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ userId }),
    });
    return handleApiResponse(response);
  },

  exchangePlaidPublicToken: async (publicToken) => {
    const baseUrl = getApiBaseUrl();
    const response = await fetchWithAuth(`${baseUrl}/api/plaid/exchange-public-token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ public_token: publicToken }),
    });
    return handleApiResponse(response);
  },

  getPlaidInstitutions: async () => {
    const baseUrl = getApiBaseUrl();
    const response = await fetchWithAuth(`${baseUrl}/api/plaid/institutions`);
    return handleApiResponse(response);
  },

  syncPlaidTransactions: async (institutionId, options = {}) => {
    const baseUrl = getApiBaseUrl();
    const { startDate, endDate, skipCategorization } = options;
    const response = await fetchWithAuth(`${baseUrl}/api/plaid/sync-transactions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        institutionId,
        startDate,
        endDate,
        skipCategorization: skipCategorization || false,
      }),
    });
    return handleApiResponse(response);
  },

  deletePlaidInstitution: async (institutionId) => {
    const baseUrl = getApiBaseUrl();
    const response = await fetchWithAuth(`${baseUrl}/api/plaid/institutions/${institutionId}`, {
      method: "DELETE",
    });
    return handleApiResponse(response);
  },

  // Pantry image analysis
  analyzePantryImage: async (imageFile) => {
    // Validate file before upload
    if (!imageFile) {
      throw new Error('No file provided');
    }
    
    // Check if it's a valid File or Blob
    const isFile = imageFile instanceof File;
    const isBlob = imageFile instanceof Blob;
    if (!isFile && !isBlob) {
      throw new Error('Invalid file type. Please select a valid image file.');
    }
    
    // Check file size (10MB limit)
    if (imageFile.size > 10 * 1024 * 1024) {
      throw new Error('Image file is too large. Maximum size is 10MB.');
    }
    
    if (imageFile.size === 0) {
      throw new Error('File is empty. Please select a valid image file.');
    }
    
    // Check file type
    if (!imageFile.type || !imageFile.type.startsWith('image/')) {
      throw new Error('Invalid file type. Please select an image file.');
    }
    
    const baseUrl = getApiBaseUrl();
    
    // Convert file to base64 to avoid multipart/form-data Content-Length issues
    let base64String;
    let mimeType = imageFile.type || 'image/jpeg';
    
    try {
      // Use FileReader for base64 conversion - it handles large files efficiently
      base64String = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          // FileReader.result is a data URL like "data:image/jpeg;base64,..."
          // Extract just the base64 part
          const dataUrl = reader.result;
          const base64Data = dataUrl.split(',')[1];
          if (!base64Data) {
            reject(new Error('Failed to extract base64 data from FileReader result'));
            return;
          }
          resolve(base64Data);
        };
        reader.onerror = () => {
          reject(new Error('FileReader failed to read file'));
        };
        reader.readAsDataURL(imageFile);
      });
      
      console.log('File converted to base64:', {
        originalSize: imageFile.size,
        base64Length: base64String.length,
        mimeType: mimeType,
      });
    } catch (conversionError) {
      throw new Error(`Failed to convert image to base64: ${conversionError.message}`);
    }

    // Increased timeout to 2 minutes for AI image analysis
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2 * 60 * 1000); // 2 minutes

    try {
      const response = await fetchWithAuth(`${baseUrl}/api/pantry/analyze-image`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          image: base64String,
          mimeType: mimeType,
          fileName: imageFile.name || 'image.jpg',
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      
      // Check for specific error responses
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          // If response isn't JSON, get text
          const text = await response.text();
          console.error('Server error response (non-JSON):', text);
          throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
        }
        
        console.error('Server error response:', errorData);
        
        if (errorData.error === 'Upload incomplete') {
          throw new Error('The upload was interrupted. Please check your connection and try again.');
        }
        if (errorData.error === 'File too large') {
          throw new Error('Image file is too large. Maximum size is 10MB.');
        }
        if (errorData.error === 'No image file provided') {
          throw new Error('No file was received by the server. Please try again.');
        }
        throw new Error(errorData.details || errorData.error || `Upload failed: ${response.statusText}`);
      }
      
      return handleApiResponse(response);
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Image analysis timed out after 2 minutes. The image may be too complex or the server is busy. Please try again.');
      }
      // Re-throw with better message if it's already an Error with a message
      if (error.message) {
        throw error;
      }
      throw new Error('Network error during image upload. Please check your connection and try again.');
    }
  },
};
